import { createTheme } from '@mui/material/styles';
import { makeStyles } from "@mui/styles";
import CloseIcon from '@mui/icons-material/Close';
import { AppBar, Button, Dialog, DialogActions, IconButton, Paper, Slide, Stack, Toolbar, Typography } from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import _ from "lodash";
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import ColorConstants from '../../defaults/ColorConstants';
import FieldNames from '../../defaults/FieldNames.json';
import { setActivities } from '../../redux/slices/activitiesSlice';
import { setActualsActivities } from '../../redux/slices/actualsActivitiesSlice';
import { setActualsTreatments } from '../../redux/slices/actualsTreatmentsSlice';
import { setTreatments } from '../../redux/slices/treatmentsSlice';
import ErrorService from '../../services/ErrorService';
import FeatureService from '../../services/FeatureService';
import GroupingService from '../../services/GroupingService';
import PlannedActualsService from '../../services/PlannedActualsService';
import { createFeatureObject, isUpdateFullySuccessful, isUpdatePartiallySuccessful } from '../../utils/FeatureHelper';
import { getAttachedFeaturesUpdate } from '../../utils/GroupingHelper';
import BasicTextField from '../details/customPanels/BasicTextField';
import DraggableList from './DraggableList';
import {sortFeatureAttributesByDate} from '../../utils/DateHelper';
import {setListCheckedFeatures} from "../../redux/slices/displayStateSlice";

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const useStyles = makeStyles(() => ({
  flexPaper: {
    flex: 1,
    padding: 16,
    backgroundColor: ColorConstants.LightGrayAlien
  },
  root: {
    display: 'flex',
    flexWrap: 'wrap',
    backgroundColor: ColorConstants.LightGrayAlien
  }
}));

const theme = createTheme({
  overrides: {
    
  },
});

export default function AssociateDialog({
   associateHandleClose,
   associateOpen,
   associatedCount,
   setAssociatedCount,
   isLoading,
   treatments,
   activities,
   currentFeature,
   setCurrentFeature,
   actualTreatments,
   actualActivities,
   setIsLoading, 
   isFromList
}) {

  const isInitialLoad = false;
  const classes = useStyles();
  const [allSelectedFeatures, setAllSelectedFeatures] = useState([]);
  const [groupName, setGroupName] = useState("");
  const dispatch = useDispatch();
  const config = useSelector((state) => state.configuration.config);
  const rootServiceUrl = useSelector((state) => state.configuration.rootServiceUrl);
  const filterGeometry = useSelector((state) => state.filter.filterGeometry);
  const prevData = useRef({ activities, treatments, actualActivities, actualTreatments }).current;
  const definitionExpression = useSelector((state) => state.filter.definitionExpression);
  const listCheckedFeatures = useSelector((state) => state.displayState.listCheckedFeatures);

  useEffect(() => {
    return () => {
      prevData.activities = activities;
      prevData.treatments = treatments;
      prevData.actualActivities = actualActivities;
      prevData.actualTreatments = actualTreatments;
    };
  }, [activities, treatments, actualTreatments, actualActivities, filterGeometry, prevData]);

  useEffect(() => {
    if(associateOpen && isFromList){
      getAttachedFeaturesUpdate(listCheckedFeatures, setAllSelectedFeatures, setIsLoading);
    } else if(associateOpen && !isFromList){
      if(filterGeometry){
        let tempFeatures = [];
        // TODO: This seems incorrect, but not seeing any system issues
        FeatureService.getAllFeaturesUntilDone(filterGeometry, FieldNames.joinFeatureValues.polygon, definitionExpression, "all")
        .then(featureResp => {
          if(featureResp && featureResp.length > 0){
            featureResp.forEach((feature) => {
              let obj = createFeatureObject(feature, null);
              tempFeatures.push(obj);
            });
          }
    
          let currentObj;
          //This should add the current feature we are on
          const currentFeatureAttr = (currentFeature && currentFeature.attributes) ? currentFeature.attributes : currentFeature;
          if(currentFeatureAttr){
            currentObj = createFeatureObject(currentFeatureAttr, null);
          }
    
          let editableFeature = [];
          if(currentObj){
            editableFeature = tempFeatures.filter(x => x[FieldNames.treatmentActivityGroupItems.entityID] === currentObj.ID || x.ID === currentObj.ID);
          }
          if(!editableFeature || (editableFeature && editableFeature.length === 0)){
            tempFeatures.push(currentObj);
          }

          sortFeatureAttributesByDate(tempFeatures, FieldNames.featureFields.initiationDate, false)
          getAttachedFeaturesUpdate(tempFeatures, setAllSelectedFeatures, setIsLoading);
        }).catch(error => {
          ErrorService.addErrorLogEntry(error);
        });
      }
    }
  }, [filterGeometry, config, rootServiceUrl, setIsLoading, associateOpen]);

  const adjustItems = (tempCurrentFeature, isCurrentTreatment, isCurrentActual, isTreatment, isActual, idField, featureIds, currentFeatureIdField, fields) => {
    let updatedItems = [];
    let features;
    if(isTreatment){
      if(isActual){
        features = actualTreatments;
      } else {
        features = treatments;
      }
    } else {
      if(isActual){
        features = actualActivities;
      } else {
        features = activities;
      }
    }
    let removeCurrentFeature = features;
    const tempFeatures = removeCurrentFeature.map(feature => {
      //Includes the current item if the current item matches the isTreatment, isActual
      let newFeature = _.cloneDeep(feature);
      if(featureIds && featureIds.includes(feature[idField])){
        let count = feature[fields.groupCount];
        if(!feature[fields.groupCount]){
          count = 0;
        }
        newFeature[fields.groupCount] = count + 1;
        newFeature[fields.hasGroup] = FieldNames.yesNo.yes;
        updatedItems.push(newFeature);
      }
      return newFeature
    })
    FeatureService.updateMultipleFeatures(updatedItems).then(resp => {
      let isAtLeastPartiallySuccessful = isUpdatePartiallySuccessful(resp);
      let isFullySuccessful = isUpdateFullySuccessful(resp);
      if(isAtLeastPartiallySuccessful || isFullySuccessful){
        if(isTreatment){
          if(isActual){
            dispatch(setActualsTreatments(tempFeatures));
          } else {
            dispatch(setTreatments(tempFeatures));
          }
        } else {
          if(isActual){
            dispatch(setActualsActivities(tempFeatures));
          } else {
            dispatch(setActivities(tempFeatures));
          }
        }
      }
    }).catch(error => {
      ErrorService.addErrorLogEntry(error);
    });
  }

  const handleSaveGrouping = () => {
    GroupingService.createGroup(allSelectedFeatures, groupName).then((groupResp) => {
      if(groupResp && groupResp.addResults && groupResp.addResults.length > 0 && groupResp.addResults[0].success){
        associateHandleClose(true);
        const newCount = associatedCount + 1;
        setAssociatedCount(newCount);
        let tempCurrentFeature = Object.assign({}, currentFeature);
        let tempCurrentFeatureAttr = (tempCurrentFeature && tempCurrentFeature.attributes) ? tempCurrentFeature.attributes : tempCurrentFeature;
        let isCurrentTreatment = true;
        let isCurrentActual = false;
        let currentFeatureIdField;
        let currentFeatureAttr = (currentFeature && currentFeature.attributes) ? currentFeature.attributes : currentFeature;
        if(currentFeatureAttr){
          currentFeatureIdField = FieldNames.featureFields.id;
          tempCurrentFeatureAttr[FieldNames.featureFields.groupCount] = tempCurrentFeatureAttr[FieldNames.featureFields.groupCount] + 1;
          tempCurrentFeatureAttr[FieldNames.featureFields.hasGroup] = FieldNames.yesNo.yes;
          if(currentFeatureAttr[FieldNames.featureFields.class] === FieldNames.classTypes.treatment){
            isCurrentTreatment = true;
            isCurrentActual = false;
          } else if(currentFeatureAttr[FieldNames.featureFields.class] === FieldNames.classTypes.activity){
            isCurrentTreatment = false;
            isCurrentActual = false;
            currentFeatureIdField = FieldNames.featureFields.id;
          } else if(currentFeatureAttr[FieldNames.featureFields.class] === FieldNames.classTypes.actualTreatment){
            isCurrentTreatment = true;
            isCurrentActual = true;
          } else if(currentFeatureAttr[FieldNames.featureFields.class] === FieldNames.classTypes.actualActivity){
            isCurrentTreatment = false;
            isCurrentActual = true;
          }
          if(tempCurrentFeature && tempCurrentFeature.attributes){
            tempCurrentFeature.attributes = tempCurrentFeatureAttr;
          } else {
            tempCurrentFeature = tempCurrentFeatureAttr;
          }
          setCurrentFeature(tempCurrentFeature)
        }
        //Now that we have created a new group we need to adjust all relevant items
        //Using allSelectedFeatures (local state) because it reflects any items the user removed in this dialog before saving
        const selectedTreatments = allSelectedFeatures.filter(x => x[FieldNames.featureFields.class] === FieldNames.classTypes.treatment);
        const treatmentIds = selectedTreatments.map(x => x[FieldNames.featureFields.id]);
        const selectedActivities = allSelectedFeatures.filter(x => x[FieldNames.featureFields.class] === FieldNames.classTypes.activity);
        const activityIds = selectedActivities.map(x => x[FieldNames.featureFields.id]);
        const selectedActualTreatments = allSelectedFeatures.filter(x => x[FieldNames.featureFields.class] === FieldNames.classTypes.actualTreatment);
        const actualTreatmentIds = selectedActualTreatments.map(x => x[FieldNames.featureFields.id]);
        const selectedActualActivities = allSelectedFeatures.filter(x => x[FieldNames.featureFields.class] === FieldNames.classTypes.actualActivity);
        const actualActivityIds = selectedActualActivities.map(x => x[FieldNames.featureFields.id]);

        if(selectedTreatments && selectedTreatments.length > 0){
          adjustItems(tempCurrentFeature, isCurrentTreatment, isCurrentActual, true, false, FieldNames.featureFields.id, treatmentIds, currentFeatureIdField, FieldNames.featureFields)
        } 

        if(selectedActivities && selectedActivities.length > 0){
          adjustItems(tempCurrentFeature, isCurrentTreatment, isCurrentActual, false, false, FieldNames.featureFields.id, activityIds, currentFeatureIdField, FieldNames.featureFields)
        } 

        if(selectedActualTreatments && selectedActualTreatments.length > 0){
          adjustItems(tempCurrentFeature, isCurrentTreatment, isCurrentActual, true, true, FieldNames.featureFields.id, actualTreatmentIds, currentFeatureIdField, FieldNames.featureFields)
        } 

        if(selectedActualActivities && selectedActualActivities.length > 0){
          adjustItems(tempCurrentFeature, isCurrentTreatment, isCurrentActual, false, true, FieldNames.featureFields.id, actualActivityIds, currentFeatureIdField, FieldNames.featureFields)
        } 


        if((!selectedActivities || selectedActivities.length === 0) && (!selectedTreatments || selectedTreatments.length === 0) && 
        (!selectedActualTreatments || selectedActualTreatments.length === 0) && (!selectedActualActivities || selectedActualActivities.length === 0)){

          dispatch(setListCheckedFeatures([]))
        }
        setGroupName("");
      }
    }).catch(error => {
      ErrorService.addErrorLogEntry(error);
    });
  }

  const reorder = (list, startIndex, endIndex) => {
    const result = Array.from(list);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);
  
    return result;
  };

  const onDragEnd = (result) => {
    // `destination` is `undefined` if the item was dropped outside the list
    // In this case, do nothing
    if (!result.destination) {
      return;
    }

    const freshItems = reorder(
      allSelectedFeatures,
      result.source.index,
      result.destination.index
    );
    setAllSelectedFeatures(freshItems);
  }

  const handleRemoval = (e, feature) => {
    const isEstimate = (feature[FieldNames.treatmentActivityGroupItems.entityType] === FieldNames.entityTypes.activity || 
      feature[FieldNames.treatmentActivityGroupItems.entityType] === FieldNames.entityTypes.treatment);

    const isEstimateWithActual = isEstimate && (feature[FieldNames.featureFields.hasActual] === FieldNames.yesNo.yes);

    if(feature && (!isEstimate || isEstimateWithActual)){
      PlannedActualsService.getAttachedFeatures(feature[FieldNames.featureFields.joinFeature], [feature[FieldNames.featureFields.joinID]], isEstimate).then(resp => {
        let finalOptionsToRemove = [];
        if(resp && resp.length > 0){
          resp.forEach(item => {
            finalOptionsToRemove.push(item[FieldNames.featureFields.objectID])
          })
        }
        const remainingGroupItems = allSelectedFeatures.filter(x => x[FieldNames.treatmentActivityGroupItems.objectID] !== feature[FieldNames.featureFields.objectID] && !finalOptionsToRemove.includes(x[FieldNames.treatmentActivityGroupItems.objectID]));
        setAllSelectedFeatures(remainingGroupItems);
      }).catch(error => {
        ErrorService.addErrorLogEntry(error);
      });
    } else {
      const remainingGroupItems = allSelectedFeatures.filter(x => x[FieldNames.treatmentActivityGroupItems.objectID] !== feature[FieldNames.featureFields.objectID]);
      setAllSelectedFeatures(remainingGroupItems);
    }
  }

  return (
    <div>
      <Dialog
        style={{ minWidth: 550 }}
        open={associateOpen}
        onClose={() => associateHandleClose(false)}
        TransitionComponent={Transition}
      >
        <AppBar sx={{ position: 'relative' }} style={{backgroundColor: ColorConstants.BlueAlien}}>
          <Toolbar>
            <IconButton
              edge="start"
              color="inherit"
              onClick={() => associateHandleClose(false)}
              aria-label="close"
            >
              <CloseIcon style={{ fill: ColorConstants.WhiteAlien }} />
            </IconButton>
            <Typography sx={{ ml: 2, flex: 1 }} variant="h6" component="div" style={{ color: ColorConstants.WhiteAlien }}>
              Associate Items
            </Typography>
          </Toolbar>
        </AppBar>
        {(isLoading) ? (  <Stack alignItems="center" style={{ display: 'flex', justifyContent: 'center', height: '400px', width: '400px' }}><CircularProgress size={75} thickness={2} color={theme.palette.primary1Color} /></Stack>) : 
        (<div className={classes.root}>
          <Paper className={classes.flexPaper}>
            <DraggableList isAssociate={true} isInitialLoad={isInitialLoad}
                           items={allSelectedFeatures}
                           onDragEnd={onDragEnd} handleRemoval={handleRemoval} />
          </Paper>
        </div>)}
        
        <DialogActions>
          <div style={{height: 64, paddingTop: 10}}>
            <BasicTextField
              value={groupName}
              setValue={setGroupName}
              label={""}
              field={"GroupName"}
              styleOverride={{ marginLeft: 15, minWidth: 270 }}
            />
            <Button variant="contained" size="small" autoFocus onClick={() => handleSaveGrouping()} style={{ margin: 10}}>
              Save
            </Button>
            <Button variant="contained" size="small" autoFocus onClick={() => associateHandleClose(false)} style={{ margin: 10}}>
              Cancel
            </Button>
          </div>
        </DialogActions>
      </Dialog>
    </div>
  );
}